"use client";
import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { useNavigate } from 'react-router-dom';
import { Check, MessageCircle, UserPlus, Users } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { useToast } from "@/hooks/use-toast"

const ReferralVerified = () => {
  const [isGroupChatCreated, setIsGroupChatCreated] = useState(false)
  const [referralData, setReferralData] = useState(null)
  const navigate = useNavigate()
  const { toast } = useToast()

  // Current user data - in a real app, this would come from authentication
  const currentUser = {
    id: "1",
    name: "Alex Morgan",
    email: "alex@example.com",
    avatar: "/placeholder.svg?height=40&width=40",
    role: "Product Manager",
    company: "TechCorp",
  }

  useEffect(() => {
    // Get referral data from localStorage
    const storedData = localStorage.getItem("referralData")
    if (storedData) {
      setReferralData(JSON.parse(storedData))
    } else {
      // If no data, redirect to create page
      navigate("/dashboard/create-referral")
    }
  }, [navigate])

  const handleCreateGroupChat = () => {
    // In a real app, this would call an API to create a group chat
    setTimeout(() => {
      setIsGroupChatCreated(true)
      toast({
        title: "Group chat created!",
        description: "You can now start the conversation.",
      })
    }, 1000)
  }

  const handleEnterChat = () => {
    navigate("/dashboard/group-chat")
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-green-50 to-white p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto">
        <div className="flex justify-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="bg-green-100 rounded-full p-4">
            <div className="bg-green-500 rounded-full p-3">
              <Check className="h-8 w-8 text-white" />
            </div>
          </motion.div>
        </div>

        <Card className="shadow-lg border-green-100">
          <CardHeader className="text-center bg-green-50 rounded-t-lg">
            <CardTitle className="text-2xl text-green-800">Referral Verified!</CardTitle>
            <CardDescription className="text-green-700">
              You've successfully created a professional connection
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6 pb-4">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex flex-col md:flex-row items-center justify-center w-full">
                <PersonCard person={currentUser} label="You" />

                <div className="my-4 md:my-0 md:mx-4 rotate-90 md:rotate-0">
                  <motion.div
                    animate={{ x: [0, 10, 0] }}
                    transition={{ repeat: Number.POSITIVE_INFINITY, duration: 2, repeatType: "reverse" }}>
                    <UserPlus className="h-6 w-6 text-green-500" />
                  </motion.div>
                </div>

                <PersonCard
                  person={
                    referralData?.friend || {
                      id: "2",
                      name: "Jamie Wilson",
                      email: "jamie@example.com",
                      avatar: "/placeholder.svg?height=40&width=40",
                      role: "UX Designer",
                      company: "DesignHub",
                    }
                  }
                  label="Your Friend" />

                <div className="my-4 md:my-0 md:mx-4 rotate-90 md:rotate-0">
                  <motion.div
                    animate={{ x: [0, 10, 0] }}
                    transition={{ repeat: Number.POSITIVE_INFINITY, duration: 2, repeatType: "reverse", delay: 0.5 }}>
                    <UserPlus className="h-6 w-6 text-green-500" />
                  </motion.div>
                </div>

                <PersonCard
                  person={
                    referralData?.contact || {
                      id: "3",
                      name: "Taylor Reed",
                      email: "taylor@example.com",
                      avatar: "/placeholder.svg?height=40&width=40",
                      role: "Engineering Director",
                      company: "InnovateTech",
                    }
                  }
                  label="Your Contact" />
              </div>

              <Separator className="bg-green-100" />

              <div className="text-center space-y-4 w-full">
                <h3
                  className="text-lg font-medium text-green-800 flex items-center justify-center gap-2">
                  <Users className="h-5 w-5" />
                  Start a Group Conversation
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Create a group chat to facilitate the introduction between your friend and contact.
                </p>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  {!isGroupChatCreated ? (
                    <Button
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white gap-2"
                      onClick={handleCreateGroupChat}>
                      <MessageCircle className="h-5 w-5" />
                      Create Group Chat
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white gap-2"
                      onClick={handleEnterChat}>
                      <MessageCircle className="h-5 w-5" />
                      Enter Group Chat
                    </Button>
                  )}
                </motion.div>
              </div>
            </div>
          </CardContent>

          <CardFooter className="bg-green-50 rounded-b-lg flex justify-center">
            <p className="text-sm text-green-700">
              {isGroupChatCreated
                ? "Group chat is ready! You can now start the conversation."
                : "Create a group chat to facilitate this professional introduction."}
            </p>
          </CardFooter>
        </Card>

        <div className="mt-6 text-center text-gray-500 text-sm">
          <p>
            Need help with your referral?{" "}
            <a href="#" className="text-green-600 hover:underline">
              Contact support
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
}

// Helper component for displaying a person card
const PersonCard = ({
  person,
  label
}) => (
  <motion.div
    whileHover={{ y: -5 }}
    className="flex flex-col items-center p-3 rounded-lg bg-white shadow-sm border border-green-100">
    <Badge
      variant="outline"
      className="mb-2 bg-green-50 text-green-700 border-green-200">
      {label}
    </Badge>
    <Avatar className="h-16 w-16 mb-2">
      <AvatarImage src={person.avatar} alt={person.name} />
      <AvatarFallback className="bg-green-100 text-green-800">
        {person.name
          .split(" ")
          .map((n) => n[0])
          .join("")}
      </AvatarFallback>
    </Avatar>
    <h3 className="font-medium text-gray-900">{person.name}</h3>
    <p className="text-sm text-gray-500">{person.role}</p>
    <p className="text-xs text-gray-400">{person.company}</p>
  </motion.div>
)

export default ReferralVerified
